import React from 'react';
import { X, Trash2, Mail, Calendar, Cloud, FileText } from 'lucide-react';
import './DisconnectConfirmModal.css';

export type DisconnectIntegrationType = 'email' | 'calendar' | 'cloud' | 'document';

export interface DisconnectIntegration {
  id: string;
  type: DisconnectIntegrationType;
  name: string;
  account: string;
}

export interface DisconnectConfirmModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for confirming disconnection */
  onConfirm: (id: string) => void;
  /** Integration to disconnect */
  integration: DisconnectIntegration | null;
  /** Optional custom className */
  className?: string;
  /** Labels for i18n support */
  labels?: DisconnectConfirmModalLabels;
}

export interface DisconnectConfirmModalLabels {
  title?: string;
  message?: string;
  cancel?: string;
  disconnect?: string;
}

const defaultLabels: Required<DisconnectConfirmModalLabels> = {
  title: 'Disconnect Integration',
  message: 'Are you sure you want to disconnect this integration? Hofsync will no longer be able to import data from this source.',
  cancel: 'Cancel',
  disconnect: 'Disconnect',
};

const getIntegrationIcon = (type: DisconnectIntegrationType) => {
  switch (type) {
    case 'email':
      return <Mail size={20} />;
    case 'calendar':
      return <Calendar size={20} />;
    case 'cloud':
      return <Cloud size={20} />;
    case 'document':
      return <FileText size={20} />;
    default:
      return <Mail size={20} />;
  }
};

/**
 * DisconnectConfirmModal - Confirmation dialog for disconnecting an integration.
 */
export const DisconnectConfirmModal: React.FC<DisconnectConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  integration,
  className = '',
  labels = {},
}) => {
  const l = { ...defaultLabels, ...labels };

  if (!isOpen || !integration) return null;

  const handleConfirm = () => {
    onConfirm(integration.id);
    onClose();
  };

  return (
    <div className="disconnect-confirm-modal-overlay" onClick={onClose}>
      <div
        className={`disconnect-confirm-modal ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="disconnect-confirm-modal-title"
      >
        {/* Header */}
        <div className="disconnect-confirm-modal__header">
          <div className="disconnect-confirm-modal__header-title">
            <Trash2 size={20} className="disconnect-confirm-modal__header-icon" />
            <h3 id="disconnect-confirm-modal-title" className="disconnect-confirm-modal__title">
              {l.title}
            </h3>
          </div>
          <button
            type="button"
            className="disconnect-confirm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="disconnect-confirm-modal__body">
          <p className="disconnect-confirm-modal__message">
            {l.message}
          </p>

          <div className="disconnect-confirm-modal__integration">
            <div className="disconnect-confirm-modal__integration-icon">
              {getIntegrationIcon(integration.type)}
            </div>
            <div className="disconnect-confirm-modal__integration-info">
              <p className="disconnect-confirm-modal__integration-name">
                {integration.name}
              </p>
              <p className="disconnect-confirm-modal__integration-account">
                {integration.account}
              </p>
            </div>
          </div>

          <div className="disconnect-confirm-modal__actions">
            <button
              type="button"
              className="disconnect-confirm-modal__cancel"
              onClick={onClose}
            >
              {l.cancel}
            </button>
            <button
              type="button"
              className="disconnect-confirm-modal__confirm"
              onClick={handleConfirm}
            >
              {l.disconnect}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisconnectConfirmModal;
